/**
 * ScamBlocker AI — Webhooks Sayfası (/webhooks)
 * Webhook uç noktalarını yönet, test et, teslimat geçmişini görüntüle.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Play, Plus, Trash2, Webhook } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '@/shared/lib/api/client';
import styles from './WebhooksPage.module.css';

interface WebhookEndpoint {
  id: string;
  url: string;
  events: string[];
  active: boolean;
  secret_prefix?: string;
  created_at: string;
  last_delivery_at?: string;
  last_status?: number;
}

const EVENT_TYPES = [
  'call.started',
  'call.ended',
  'alert.created',
  'alert.critical',
  'scam.detected',
  'billing.updated',
];

const WEBHOOK_API = '/api/org/webhooks';

export function WebhooksPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [formUrl, setFormUrl] = useState('');
  const [formEvts, setFormEvts] = useState<string[]>([]);

  const { data, isLoading } = useQuery<{ webhooks: WebhookEndpoint[] }>({
    queryKey: ['webhooks'],
    queryFn: async () => {
      const { data } = await apiClient.get(WEBHOOK_API);
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      await apiClient.post(WEBHOOK_API, { url: formUrl, events: formEvts });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['webhooks'] });
      setShowForm(false);
      setFormUrl('');
      setFormEvts([]);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`${WEBHOOK_API}/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhooks'] }),
  });

  const testMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.post(`${WEBHOOK_API}/${id}/ping`);
    },
  });

  const toggleEvent = (evt: string) => {
    setFormEvts((prev) => (prev.includes(evt) ? prev.filter((e) => e !== evt) : [...prev, evt]));
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <Webhook size={22} className={styles.titleIcon} />
          <h1 className={styles.title}>Webhooks</h1>
        </div>
        <button type="button" className={styles.createBtn} onClick={() => setShowForm((v) => !v)}>
          <Plus size={16} /> Yeni Webhook
        </button>
      </div>

      {/* Yeni webhook formu */}
      {showForm && (
        <div className={styles.form}>
          <div className={styles.formRow}>
            <label className={styles.label} htmlFor="webhook-endpoint-url">
              {t('webhooks.endpointUrl')}
            </label>
            <input
              id="webhook-endpoint-url"
              className={styles.input}
              type="url"
              placeholder="https://api.sirketiniz.com/webhook"
              value={formUrl}
              onChange={(e) => setFormUrl(e.target.value)}
            />
          </div>
          <div className={styles.formRow}>
            <span className={styles.label}>{t('webhooks.events')}</span>
            <div className={styles.evtGrid}>
              {EVENT_TYPES.map((evt) => (
                <label key={evt} className={styles.evtLabel}>
                  <input
                    type="checkbox"
                    checked={formEvts.includes(evt)}
                    onChange={() => toggleEvent(evt)}
                  />
                  <code className={styles.evtCode}>{evt}</code>
                </label>
              ))}
            </div>
          </div>
          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.confirmBtn}
              disabled={!formUrl || formEvts.length === 0 || createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending ? t('webhooks.saving') : t('webhooks.save')}
            </button>
            <button type="button" className={styles.cancelBtn} onClick={() => setShowForm(false)}>
              {t('webhooks.cancel')}
            </button>
          </div>
          {createMutation.isError && <p className={styles.errMsg}>{t('webhooks.saveError')}</p>}
        </div>
      )}

      {isLoading && (
        <div className={styles.center}>
          <span className={styles.spinner} />
        </div>
      )}

      {!isLoading && (
        <div className={styles.list}>
          {(!data?.webhooks || data.webhooks.length === 0) && (
            <p className={styles.empty}>{t('webhooks.empty')}</p>
          )}
          {data?.webhooks.map((wh) => (
            <div key={wh.id} className={`${styles.whCard} ${!wh.active ? styles.inactive : ''}`}>
              <div className={styles.whInfo}>
                <span className={styles.whUrl}>{wh.url}</span>
                <div className={styles.whEvts}>
                  {wh.events.map((e) => (
                    <code key={e} className={styles.evtBadge}>
                      {e}
                    </code>
                  ))}
                </div>
                {wh.last_delivery_at && (
                  <span className={styles.whMeta}>
                    {t('webhooks.lastDelivery', {
                      date: new Date(wh.last_delivery_at).toLocaleString('tr-TR'),
                    })}
                    {wh.last_status && (
                      <span
                        style={{
                          color:
                            wh.last_status < 300 ? 'var(--color-success)' : 'var(--color-danger)',
                        }}
                      >
                        {' '}
                        · HTTP {wh.last_status}
                      </span>
                    )}
                  </span>
                )}
              </div>
              <div className={styles.whActions}>
                <button
                  type="button"
                  className={styles.testBtn}
                  onClick={() => testMutation.mutate(wh.id)}
                  title={t('webhooks.testPingTitle')}
                >
                  <Play size={14} />
                </button>
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={() => {
                    if (confirm("Bu webhook'u silmek istediğinize emin misiniz?")) {
                      deleteMutation.mutate(wh.id);
                    }
                  }}
                  title={t('webhooks.deleteTitle')}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
